import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ResizableTruncatableCell from "./ResizableTruncatableCell";

const isColorDark = (hex: string): boolean => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

const pastelColorFromString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 80%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (
      parts[0][0].toUpperCase() +
      parts[parts.length - 1][0].toUpperCase()
    );
  }
  return name.slice(0, 2).toUpperCase();
};

const EyeIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12S5.25 4.5 12 4.5 21.75 12 21.75 12 18.75 19.5 12 19.5 2.25 12 2.25 12z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
    />
  </svg>
);

const EyeOffIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 3l18 18"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12S5.25 4.5 12 4.5c2.379 0 4.339.786 5.88 1.86M21.75 12s-3 7.5-9.75 7.5c-2.379 0-4.339-.786-5.88-1.86"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.177 9.177A3.75 3.75 0 0012 15.75a3.74 3.74 0 002.823-1.245"
    />
  </svg>
);

const ChatIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12.75v6.75l3-3h5.25a7.5 7.5 0 100-15 7.5 7.5 0 00-7.5 7.5z"
    />
  </svg>
);


interface TaskListDashboardProps {
  onBack: () => void;
}

interface Column {
  id: string;
  title: string;
  width?: number;
  type: "text" | "person" | "status" | "date";
}

interface StatusOption {
  label: string;
  color: string;
}

interface TaskAttachment {
  name: string;
  url: string;
}

interface TaskComment {
  id: string;
  author: string;
  text: string;
  timestamp: string;
  attachments: TaskAttachment[];
}

interface Person {
  id: string;
  name: string;
  color: string;
}

interface SubTask {
  id: string;
  text: string;
  done?: boolean;
  status?: string;
}

interface Task {
  id: string;
  text: string;
  extras: Record<string, string>;
  comments?: TaskComment[];
  subTasks?: SubTask[];
}

interface TaskGroup {
  id: string;
  title: string;
  color: string;
  tasks: Task[];
  columns?: Column[];
  taskWidth?: number;
  statusWidth?: number;
  statusIndex?: number;
}

interface SubSection {
  id: string;
  title: string;
  columns: Column[];
  groups: TaskGroup[];
  taskWidth?: number;
  statusWidth?: number;
  statusIndex?: number;
}

interface Section {
  id: string;
  title: string;
  color: string;
  subsections: SubSection[];
  customStatusOptions: StatusOption[];
}

const defaultSections: Section[] = [];

const defaultStatusOptions: StatusOption[] = [
  { label: "", color: "#dedede" },
  { label: "Working on it", color: "#edbd15" },
  { label: "Stuck", color: "#d13643" },
  { label: "Done", color: "#64c368" },
];
const defaultLabelSet = new Set(defaultStatusOptions.map((o) => o.label));
const ADD_LABEL_VALUE = "__add_label__";
const DEFAULT_BLANK_LABEL_COLOR = defaultStatusOptions[0].color;

const getSectionStatusOptions = (
  sections: Section[],
  sectionId: string,
): StatusOption[] => {
  const section = sections.find((s) => s.id === sectionId);
  return section
    ? [...defaultStatusOptions, ...(section.customStatusOptions || [])]
    : defaultStatusOptions;
};

const getSectionCustomOptions = (
  sections: Section[],
  sectionId: string,
): StatusOption[] => {
  const section = sections.find((s) => s.id === sectionId);
  return section?.customStatusOptions || [];
};

const renderTextWithLinks = (text: string): React.ReactNode => {
  const regex = /\[([^\]]+)\]\(((?:https?:\/\/|www\.)[^\s]+)\)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const url = match[2];
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    parts.push(
      <a
        key={match.index}
        href={href}
        className="text-blue-600 underline"
        target="_blank"
        rel="noreferrer"
      >
        {match[1]}
      </a>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const toInputDate = (val: string): string => {
  const match = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const [, d, m, y] = match;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const fromInputDate = (val: string): string => {
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, y, m, d] = match;
  return `${d}/${m}/${y}`;
};

const TaskListDashboard: React.FC<TaskListDashboardProps> = ({ onBack }) => {
  const { user } = useAuth0();
  const [sections, setSections] = useState<Section[]>(defaultSections);

  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const [activeSection, setActiveSection] = useState<string>("smid-report");
  const [activeSub, setActiveSub] = useState<{
    sectionId: string;
    subId: string;
  } | null>(null);
const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [dragTask, setDragTask] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    index: number;
  } | null>(null);
  const [dragOver, setDragOver] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    index: number;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editColor, setEditColor] = useState("#6b7280");
  const [editingSub, setEditingSub] = useState<{
    sectionId: string;
    subId: string;
  } | null>(null);
  const [editSubTitle, setEditSubTitle] = useState("");
  const [labelPopup, setLabelPopup] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    columnId: string;
    subTaskId?: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<
    | { type: "section"; sectionId: string }
    | { type: "sub"; sectionId: string; subId: string }
    | { type: "group"; sectionId: string; subId: string; groupId: string }
    | { type: "column"; sectionId: string; subId: string; groupId: string; columnId: string }
    | { type: "comment"; sectionId: string; subId: string; groupId: string; taskId: string; commentId: string }
    | null
  >(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("#dedede");
  const [commentPopup, setCommentPopup] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
  } | null>(null);
  const [newCommentText, setNewCommentText] = useState("");
  const [newCommentFiles, setNewCommentFiles] = useState<FileList | null>(null);
  const [editingComment, setEditingComment] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    commentId: string;
  } | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [editCommentFiles, setEditCommentFiles] = useState<FileList | null>(null);
  const [editingGroup, setEditingGroup] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
  } | null>(null);
  const [editGroupTitle, setEditGroupTitle] = useState("");
  const [editGroupColor, setEditGroupColor] = useState("#e5e7eb");
  const [editingColumn, setEditingColumn] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    columnId: string;
  } | null>(null);
  const [editColumnTitle, setEditColumnTitle] = useState("");
  const [editingTask, setEditingTask] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
  } | null>(null);
  const [editingExtra, setEditingExtra] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    columnId: string;
  } | null>(null);
  const [openSubTasks, setOpenSubTasks] = useState<Set<string>>(new Set());
  const [editingSubTask, setEditingSubTask] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    subTaskId: string;
  } | null>(null);
  const [selectedSubTasks, setSelectedSubTasks] =
    useState<Set<string>>(new Set());
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);
  const [moveTargetGroup, setMoveTargetGroup] = useState<string>("");
  const [moveContext, setMoveContext] = useState<{
    sectionId: string;
    subId: string;
  } | null>(null);

  const [dragSubTask, setDragSubTask] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    subTaskId: string;
    index: number;
  } | null>(null);
  const [dragSubTaskOver, setDragSubTaskOver] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    index: number;
  } | null>(null);

  const [dragColumn, setDragColumn] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    columnId: string;
    index: number;
  } | null>(null);
  const [dragColumnOver, setDragColumnOver] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    index: number;
  } | null>(null);

  const [dragGroup, setDragGroup] = useState<{
    sectionId: string;
    subId: string;
    index: number;
  } | null>(null);
  const [dragGroupOver, setDragGroupOver] = useState<{
    sectionId: string;
    subId: string;
    index: number;
  } | null>(null);

  const [people, setPeople] = useState<Person[]>([]);
  const [personPopup, setPersonPopup] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
    taskId: string;
    columnId: string;
  } | null>(null);

  const [sortState, setSortState] = useState<Record<string, "asc" | "desc">>({});

  const [columnPopup, setColumnPopup] = useState<{
    sectionId: string;
    subId: string;
    groupId: string;
  } | null>(null);
  const [newColumnType, setNewColumnType] = useState<
    "text" | "person" | "status" | "date"
  >("text");

  const [linkPopup, setLinkPopup] = useState<{
    url: string;
    onInsert: (display: string) => void;
  } | null>(null);
  const [linkDisplay, setLinkDisplay] = useState("");

  const handleLinkPaste = (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (val: string) => void,
  ) => {
    const text = e.clipboardData.getData("text");
    if (/^(https?:\/\/|www\.)/i.test(text)) {
      e.preventDefault();
      const target = e.currentTarget as HTMLInputElement | HTMLTextAreaElement;
      const value = target.value;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || start;
      setLinkDisplay("");
      setLinkPopup({
        url: text,
        onInsert: (display) => {
          const formatted = `[${display}](${text})`;
          const newVal = value.slice(0, start) + formatted + value.slice(end);
          setter(newVal);
        },
      });
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const resizing = useRef(false);
  const headerRefs = useRef<Record<string, HTMLTableHeaderCellElement | null>>({});
  const columnResizing = useRef<{
    sectionId: string;
    subId: string;
    groupId: string | null;
    columnId: string;
    startX: number;
    startWidth: number;
    minWidth: number;
  } | null>(null);
  const newCommentFileRef = useRef<HTMLInputElement>(null);
  const editCommentFileRef = useRef<HTMLInputElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(25); // percent
  const lastStateRef = useRef<string>("{}");
  const versionRef = useRef<number>(0);
  const [loaded, setLoaded] = useState(false);

  const saveTaskList = (override?: Partial<{
    sections: Section[];
    openSections: Set<string>;
    openGroups: Set<string>;
    openSubTasks: Set<string>;
    people: Person[];
    activeSection: string;
    activeSub: { sectionId: string; subId: string } | null;
    sidebarWidth: number;
  }>) => {
    const payload = {
      sections,
      openSections: Array.from(openSections),
      openGroups: Array.from(openGroups),
      openSubTasks: Array.from(openSubTasks),
      people,
      activeSection,
      activeSub,
      sidebarWidth,
      ...override,
      ...(override?.openSections && {
        openSections: Array.from(override.openSections),
      }),
      ...(override?.openGroups && {
        openGroups: Array.from(override.openGroups),
      }),
      ...(override?.openSubTasks && {
        openSubTasks: Array.from(override.openSubTasks),
      }),
    };

    fetch("/api/tasklist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    // update the last known state so incoming SSE events
    // with the same payload won't override current UI state
    lastStateRef.current = JSON.stringify({
      ...payload,
      version: versionRef.current + 1,
    });
  };

  useEffect(() => {
    const savedWidth = localStorage.getItem("tasklist_sidebar_width");
    if (savedWidth) {
      const parsed = parseFloat(savedWidth);
      if (!isNaN(parsed)) setSidebarWidth(parsed);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("tasklist_sidebar_width", sidebarWidth.toString());
  }, [sidebarWidth]);

  const startResize = (e: React.MouseEvent) => {
    resizing.current = true;
    e.preventDefault();
  };

  const startColumnResize = (
    sectionId: string,
    subId: string,
    columnId: string,
    e: React.MouseEvent,
    groupId: string | null = null,
  ) => {
    const sub = sections
      .find((sec) => sec.id === sectionId)?.subsections.find((s) => s.id === subId);
    const group = groupId
      ? sub?.groups.find((g) => g.id === groupId)
      : undefined;
    const col = groupId
      ? group?.columns?.find((c) => c.id === columnId)
      : sub?.columns.find((c) => c.id === columnId);
    let startWidth: number | null = null;
    let minWidth = 50;
    if (columnId === "task") {
      if (groupId) startWidth = group?.taskWidth ?? sub?.taskWidth ?? 200;
      else startWidth = sub?.taskWidth ?? 200;
    } else if (columnId === "status") {
      if (groupId) startWidth = group?.statusWidth ?? sub?.statusWidth ?? 120;
      else startWidth = sub?.statusWidth ?? 120;
    } else if (col) startWidth = col.width || 150;
    if (startWidth === null) return;
    const key = colKeyFrom(sectionId, subId, groupId || "", columnId);
    if (columnId === "task" || col?.type === "text") {
      minWidth = 50;
    }
    columnResizing.current = {
      sectionId,
      subId,
      groupId,
      columnId,
      startX: e.clientX,
      startWidth,
      minWidth,
    };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    fetch("/api/tasklist")
      .then((res) => res.json())
      .then((data) => {
        if (!data) return;
        versionRef.current = data.version ?? 0;
        setSections(data.sections || defaultSections);
        setOpenSections(new Set(data.openSections || []));
        setOpenGroups(new Set(data.openGroups || []));
        setOpenSubTasks(new Set(data.openSubTasks || []));
        if (data.activeSection) setActiveSection(data.activeSection);
        if (data.activeSub) setActiveSub(data.activeSub);
        if (typeof data.sidebarWidth === "number")
          setSidebarWidth(data.sidebarWidth);
        lastStateRef.current = JSON.stringify(data);
      })
      .catch(() => {
        setSections(defaultSections);
      })
      .finally(() => {
        setLoaded(true);
      });
  }, []);

  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!loaded) return;

    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    saveTimeout.current = setTimeout(() => {
      saveTaskList();
      saveTimeout.current = null;
    }, 5000);

    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        saveTimeout.current = null;
      }
    };
  }, [loaded, sections, openSections, openGroups, openSubTasks, people, activeSection, activeSub, sidebarWidth]);

  useEffect(() => {
    const es = new EventSource("/api/tasklist/subscribe");
    es.onmessage = (e) => {
      try {
        if (e.data === lastStateRef.current) return;
        const data = JSON.parse(e.data);
        if (columnResizing.current) return;
        if (typeof data.version === "number" && data.version <= versionRef.current)
          return;
        setSections(data.sections || defaultSections);
        setOpenSections(new Set(data.openSections || []));
        setOpenGroups(new Set(data.openGroups || []));
        setOpenSubTasks(new Set(data.openSubTasks || []));
        if (data.activeSection) setActiveSection(data.activeSection);
        else setActiveSection("");
        if (data.activeSub) setActiveSub(data.activeSub);
        else setActiveSub(null);
        if (typeof data.sidebarWidth === "number") setSidebarWidth(data.sidebarWidth);
        versionRef.current = data.version ?? versionRef.current;
        lastStateRef.current = JSON.stringify(data);
      } catch (err) {
        console.error(err);
      }
    };
    return () => {
      es.close();
    };
  }, []);

  useEffect(() => {
    fetch("/api/users")
      .then((res) => res.json())
      .then((data) => {
        const list = data.map((u: any) => {
          const name = u.firstName || u.lastName
            ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
            : u.name || u.email;
          return {
            id: String(u.id),
            name,
            color: pastelColorFromString(String(u.id)),
          };
        });
        setPeople(list);
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      setSidebarWidth(Math.min(40, Math.max(10, percent)));
    };
    const onMouseUp = () => {
      resizing.current = false;
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!columnResizing.current) return;
      const { sectionId, subId, groupId, columnId, startX, startWidth, minWidth } =
        columnResizing.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + diff);
      setSections((prev) =>
        prev.map((sec) =>
          sec.id === sectionId
            ? {
                ...sec,
                subsections: sec.subsections.map((sub) => {
                  if (sub.id !== subId) return sub;
                  if (columnId === "task") {
                    if (groupId) {
                      return {
                        ...sub,
                        groups: sub.groups.map((g) =>
                          g.id === groupId ? { ...g, taskWidth: newWidth } : g,
                        ),
                      };
                    }
                    return { ...sub, taskWidth: newWidth };
                  } else if (columnId === "status") {
                    if (groupId) {
                      return {
                        ...sub,
                        groups: sub.groups.map((g) =>
                          g.id === groupId ? { ...g, statusWidth: newWidth } : g,
                        ),
                      };
                    }
                    return { ...sub, statusWidth: newWidth };
                  }
                  if (groupId) {
                    return {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              columns: (g.columns || []).map((c) =>
                                c.id === columnId ? { ...c, width: newWidth } : c,
                              ),
                            }
                          : g,
                      ),
                    };
                  }
                  return {
                    ...sub,
                    columns: sub.columns.map((c) =>
                      c.id === columnId ? { ...c, width: newWidth } : c,
                    ),
                  };
                }),
              }
            : sec,
        ),
      );
    };
    const onUp = () => {
      columnResizing.current = null;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const addSection = () => {
    const newId = `section-${Date.now()}`;
    const newSection: Section = {
      id: newId,
      title: "New Section",
      color: "#6b7280",
      subsections: [],
      customStatusOptions: [],
    };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
    setActiveSection(newId);
    setActiveSub(null);
    setEditingId(newId);
    setEditTitle("New Section");
    setEditColor("#6b7280");
    saveTaskList({ sections: updatedSections, activeSection: newId, activeSub: null });
  };

  const saveEdit = (id: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, title: editTitle, color: editColor } : s,
      ),
    );
    setEditingId(null);
  };

  const deleteSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    if (activeSection === id) {
      const first = sections.find((s) => s.id !== id);
      setActiveSection(first ? first.id : "");
    }
    if (activeSub && activeSub.sectionId === id) {
      setActiveSub(null);
    }
  };

  const moveSectionUp = (index: number) => {
    if (index <= 0) return;
    setSections((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  const moveSectionDown = (index: number) => {
    setSections((prev) => {
      if (index >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr;
    });
  };

  const toggleSection = (id: string) => {
    setActiveSection(id);
    setActiveSub(null);
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const addSubSection = (sectionId: string) => {
    const newId = `sub-${Date.now()}`;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: [
                ...s.subsections,
                {
                  id: newId,
                  title: "New Sub-section",
                  columns: [],
                  groups: [],
                  taskWidth: 200,
                  statusWidth: 120,
                },
              ],
            }
          : s,
      ),
    );
    setEditingSub({ sectionId, subId: newId });
    setEditSubTitle("New Sub-section");
  };

  const saveSubEdit = (sectionId: string, subId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId ? { ...sub, title: editSubTitle } : sub,
              ),
            }
          : s,
      ),
    );
    setEditingSub(null);
  };

  const deleteSubSection = (sectionId: string, subId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.filter((sub) => sub.id !== subId),
            }
          : s,
      ),
    );
    if (
      activeSub &&
      activeSub.sectionId === sectionId &&
      activeSub.subId === subId
    ) {
      setActiveSub(null);
    }
  };

  const moveSubUp = (sectionId: string, index: number) => {
    if (index <= 0) return;
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const arr = [...sec.subsections];
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
        return { ...sec, subsections: arr };
      }),
    );
  };

  const moveSubDown = (sectionId: string, index: number) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        if (index >= sec.subsections.length - 1) return sec;
        const arr = [...sec.subsections];
        [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
        return { ...sec, subsections: arr };
      }),
    );
  };

  const confirmSectionDelete = (id: string) => {
    setDeleteConfirm({ type: "section", sectionId: id });
  };

  const confirmSubDelete = (sectionId: string, subId: string) => {
    setDeleteConfirm({ type: "sub", sectionId, subId });
  };

  const confirmGroupDelete = (
    sectionId: string,
    subId: string,
    groupId: string,
  ) => {
    setDeleteConfirm({ type: "group", sectionId, subId, groupId });
  };

  const confirmColumnDelete = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
  ) => {
    setDeleteConfirm({ type: "column", sectionId, subId, groupId, columnId });
  };

  const confirmCommentDelete = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    commentId: string,
  ) => {
    setDeleteConfirm({
      type: "comment",
      sectionId,
      subId,
      groupId,
      taskId,
      commentId,
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "section") {
      deleteSection(deleteConfirm.sectionId);
    } else if (deleteConfirm.type === "sub") {
      deleteSubSection(deleteConfirm.sectionId, deleteConfirm.subId);
    } else if (deleteConfirm.type === "group") {
      deleteGroup(
        deleteConfirm.sectionId,
        deleteConfirm.subId,
        deleteConfirm.groupId,
      );
    } else if (deleteConfirm.type === "column") {
      deleteColumn(
        deleteConfirm.sectionId,
        deleteConfirm.subId,
        deleteConfirm.groupId,
        deleteConfirm.columnId,
      );
    } else if (deleteConfirm.type === "comment") {
      deleteComment(
        deleteConfirm.sectionId,
        deleteConfirm.subId,
        deleteConfirm.groupId,
        deleteConfirm.taskId,
        deleteConfirm.commentId,
      );
    }
    setDeleteConfirm(null);
  };

  const addGroup = (sectionId: string, subId: string) => {
    const newId = `group-${Date.now()}`;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: [
                        ...sub.groups,
                        {
                          id: newId,
                          title: "New Set",
                          color: "#e5e7eb",
                          tasks: [],
                          columns: [],
                          taskWidth: sub.taskWidth,
                          statusWidth: sub.statusWidth,
                          statusIndex: sub.statusIndex ?? 0,
                        },
                      ],
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
    setOpenGroups((prev) => new Set(prev).add(newId));
    setEditingGroup({ sectionId, subId, groupId: newId });
    setEditGroupTitle("New Set");
    setEditGroupColor("#e5e7eb");
  };

  const saveGroupEdit = (sectionId: string, subId: string, groupId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? { ...g, title: editGroupTitle, color: editGroupColor }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
    setEditingGroup(null);
  };

  const deleteGroup = (sectionId: string, subId: string, groupId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.filter((g) => g.id !== groupId),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) newSet.delete(groupId);
      else newSet.add(groupId);
      return newSet;
    });
  };

  const toggleSubTaskOpen = (taskId: string) => {
    setOpenSubTasks((prev) => {
      const set = new Set(prev);
      if (set.has(taskId)) set.delete(taskId);
      else set.add(taskId);
      return set;
    });
  };

  const addTask = (sectionId: string, subId: string, groupId: string) => {
    const newId = `task-${Date.now()}`;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: [
                                ...g.tasks,
                                {
                                  id: newId,
                                  text: "",
                                  extras: {
                                    status: "",
                                    ...Object.fromEntries(
                                      (
                                        [
                                          ...sub.columns,
                                          ...(g.columns || []),
                                        ] as Column[]
                                      ).map((c) => [c.id, ""]),
                                    ),
                                  },
                                  comments: [],
                                },
                              ],
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
    setEditingTask({ sectionId, subId, groupId, taskId: newId });
  };

  const updateTaskText = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    value: string,
  ) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId ? { ...t, text: value } : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const updateTaskExtra = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    columnId: string,
    value: string,
  ) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      extras: {
                                        ...t.extras,
                                        [columnId]: value,
                                      },
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const addSubTask = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => {
    const newId = `subtask-${Date.now()}`;
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: [
                                        ...(t.subTasks || []),
                                        { id: newId, text: "", done: false, status: "" },
                                      ],
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
    setOpenSubTasks((prev) => new Set(prev).add(taskId));
    setEditingSubTask({
      sectionId,
      subId,
      groupId,
      taskId,
      subTaskId: newId,
    });
  };

  const updateSubTaskText = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    subTaskId: string,
    value: string,
  ) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: (t.subTasks || []).map((st) =>
                                        st.id === subTaskId ? { ...st, text: value } : st,
                                      ),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
  };

  const toggleSubTaskDone = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    subTaskId: string,
  ) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: (t.subTasks || []).map((st) =>
                                        st.id === subTaskId ? { ...st, done: !st.done } : st,
                                      ),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
  };

const updateSubTaskStatus = (
  sectionId: string,
  subId: string,
  groupId: string,
  taskId: string,
  subTaskId: string,
  value: string,
) => {
    if (value === ADD_LABEL_VALUE) {
      setLabelPopup({
        sectionId,
        subId,
        groupId,
        taskId,
        columnId: "subtask",
        subTaskId,
      });
      setNewLabelName("");
      setNewLabelColor("#dedede");
      return;
    }
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: (t.subTasks || []).map((st) =>
                                        st.id === subTaskId ? { ...st, status: value } : st,
                                      ),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
  };

  const deleteSubTask = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    subTaskId: string,
  ) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: (t.subTasks || []).filter((st) => st.id !== subTaskId),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
  };

  const toggleSubTaskSelection = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    subTaskId: string,
    done: boolean,
  ) => {
    const key = subTaskKeyFrom(sectionId, subId, groupId, taskId, subTaskId);
    setSelectedSubTasks((prev) => {
      const set = new Set(prev);
      if (done) set.add(key);
      else set.delete(key);
      return set;
    });
  };

  const deleteSelectedSubTasks = () => {
    if (selectedSubTasks.size === 0) return;
    setSections((prev) =>
      prev.map((sec) => ({
        ...sec,
        subsections: sec.subsections.map((sub) => ({
          ...sub,
          groups: sub.groups.map((g) => ({
            ...g,
            tasks: g.tasks.map((t) => ({
              ...t,
              subTasks: (t.subTasks || []).filter(
                (st) =>
                  !selectedSubTasks.has(
                    subTaskKeyFrom(sec.id, sub.id, g.id, t.id, st.id),
                  ),
              ),
            })),
          })),
        })),
      }))
    );
    setSelectedSubTasks(new Set());
  };

  const handleStatusChange = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    columnId: string,
    value: string,
  ) => {
    if (value === ADD_LABEL_VALUE) {
      setLabelPopup({ sectionId, subId, groupId, taskId, columnId });
      setNewLabelName("");
      setNewLabelColor("#dedede");
      return;
    }
    updateTaskExtra(sectionId, subId, groupId, taskId, columnId, value);
  };



  const sortGroupTasks = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
    direction: "asc" | "desc",
    colType: Column["type"],
  ) => {
    const getValue = (t: Task): any => {
      const val = t.extras[columnId] || "";
      if (colType === "date") {
        const match = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [, d, m, y] = match;
          return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).getTime();
        }
        return 0;
      } else if (colType === "person") {
        return (
          people.find((p) => p.id === val)?.name.toLowerCase() || ""
        );
      }
      return (val as string).toLowerCase();
    };

    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: [...g.tasks].sort((a, b) => {
                                const av = getValue(a);
                                const bv = getValue(b);
                                if (av < bv) return direction === "asc" ? -1 : 1;
                                if (av > bv) return direction === "asc" ? 1 : -1;
                                return 0;
                              }),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : sec,
      ),
    );
  };

  const sortSubTasks = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    direction: "asc" | "desc",
  ) => {
    const getValue = (st: SubTask) => (st.status || "").toLowerCase();
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: [...(t.subTasks || [])].sort((a, b) => {
                                        const av = getValue(a);
                                        const bv = getValue(b);
                                        if (av < bv) return direction === "asc" ? -1 : 1;
                                        if (av > bv) return direction === "asc" ? 1 : -1;
                                        return 0;
                                      }),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
  };

  const subTaskSortKeyFrom = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => `${sectionId}|${subId}|${groupId}|${taskId}|subtasks`;

  const toggleSubTaskSort = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => {
    const key = subTaskSortKeyFrom(sectionId, subId, groupId, taskId);
    const dir = sortState[key] === "asc" ? "desc" : "asc";
    setSortState({ ...sortState, [key]: dir });
    sortSubTasks(sectionId, subId, groupId, taskId, dir);
  };

  const toggleSort = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
    colType: Column["type"],
  ) => {
    const key = colKeyFrom(sectionId, subId, groupId, columnId);
    const dir = sortState[key] === "asc" ? "desc" : "asc";
    setSortState({ ...sortState, [key]: dir });
    sortGroupTasks(sectionId, subId, groupId, columnId, dir, colType);
  };

  const fileToDataUrl = (file: File): Promise<TaskAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ name: file.name, url: reader.result as string });
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const addComment = async (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => {
    const attachments: TaskAttachment[] = newCommentFiles
      ? await Promise.all(
          Array.from(newCommentFiles).map((f) => fileToDataUrl(f)),
        )
      : [];
    const comment: TaskComment = {
      id: `${Date.now()}`,
      author: user?.name || "Unknown",
      text: newCommentText,
      timestamp: new Date().toISOString(),
      attachments,
    };
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      comments: t.comments
                                        ? [...t.comments, comment]
                                        : [comment],
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : sec,
      ),
    );
    setNewCommentText("");
    setNewCommentFiles(null);
  };

  const saveCommentEdit = async (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    commentId: string,
  ) => {
    const attachments: TaskAttachment[] = editCommentFiles
      ? await Promise.all(
          Array.from(editCommentFiles).map((f) => fileToDataUrl(f)),
        )
      : [];
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      comments: t.comments?.map((c) =>
                                        c.id === commentId
                                          ? {
                                              ...c,
                                              text: editCommentText,
                                              attachments: [...c.attachments, ...attachments],
                                            }
                                          : c,
                                      ),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : sec,
      ),
  );
  setEditingComment(null);
  setEditCommentText("");
  setEditCommentFiles(null);
  };

  const deleteComment = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    commentId: string,
  ) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      comments: t.comments?.filter((c) => c.id !== commentId),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : sec,
      ),
    );
  };

  const deleteTask = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.filter((t) => t.id !== taskId),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const handleTaskDrop = (
    targetSectionId: string,
    targetSubId: string,
    targetGroupId: string,
    targetIndex: number,
  ) => {
    if (!dragTask) return;
    if (
      dragTask.sectionId !== targetSectionId ||
      dragTask.subId !== targetSubId
    ) {
      setDragTask(null);
      return;
    }
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === targetSectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) => {
                if (sub.id !== targetSubId) return sub;
                let moved: Task | null = null;
                const groupsAfterRemoval = sub.groups.map((g) => {
                  if (g.id !== dragTask.groupId) return g;
                  const newTasks = [...g.tasks];
                  const [removed] = newTasks.splice(dragTask.index, 1);
                  if (removed) moved = removed;
                  return { ...g, tasks: newTasks };
                });
                if (!moved) return { ...sub, groups: groupsAfterRemoval };
                return {
                  ...sub,
                  groups: groupsAfterRemoval.map((g) => {
                    if (g.id !== targetGroupId) return g;
                    const newTasks = [...g.tasks];
                    let insertIndex = targetIndex;
                    if (
                      dragTask.groupId === targetGroupId &&
                      dragTask.index < targetIndex
                    ) {
                      insertIndex = targetIndex - 1;
                    }
                    newTasks.splice(insertIndex, 0, moved as Task);
                    return { ...g, tasks: newTasks };
                  }),
                };
              }),
            }
          : sec,
      ),
    );
    setDragTask(null);
    setDragOver(null);
  };

  const handleColumnDrop = (
    sectionId: string,
    subId: string,
    groupId: string,
    targetIndex: number,
  ) => {
    if (!dragColumn) return;
    if (
      dragColumn.sectionId !== sectionId ||
      dragColumn.subId !== subId ||
      (dragColumn.columnId !== "status" && dragColumn.groupId !== groupId)
    ) {
      setDragColumn(null);
      setDragColumnOver(null);
      return;
    }
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) => {
                if (sub.id !== subId) return sub;

                // Moving status column relative to a group column
                if (dragColumn.columnId === "status") {
                  const targetGroupId = groupId || dragColumn.groupId;
                  if (targetGroupId) {
                    const grp = sub.groups.find((g) => g.id === targetGroupId);
                    const base = [...sub.columns, ...(grp?.columns || [])];
                    const newIdx = Math.min(targetIndex, base.length);
                    return {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === targetGroupId ? { ...g, statusIndex: newIdx } : g,
                      ),
                    };
                  }
                }

                if (groupId) {
                  // Reordering group specific columns
                  return {
                    ...sub,
                    groups: sub.groups.map((g) => {
                      if (g.id !== groupId) return g;
                      const cols = [...(g.columns || [])];
                      const fromIndex = cols.findIndex((c) => c.id === dragColumn.columnId);
                      if (fromIndex === -1) return g;
                      const [moved] = cols.splice(fromIndex, 1);
                      let insertIndex = targetIndex;
                      if (fromIndex < targetIndex) insertIndex = targetIndex - 1;
                      cols.splice(insertIndex, 0, moved);
                      return { ...g, columns: cols };
                    }),
                  };
                }

                // Moving columns within subsection (including status)
                const withStatus = [...sub.columns];
                const targetGroup = sub.groups.find((g) => g.id === dragColumn.groupId);
                const currentIdx = targetGroup?.statusIndex ?? sub.statusIndex ?? 0;
                withStatus.splice(currentIdx, 0, { id: "status", title: "Status", type: "status" });
                const fromIndex = withStatus.findIndex((c) => c.id === dragColumn.columnId);
                if (fromIndex === -1) return sub;
                const [moved] = withStatus.splice(fromIndex, 1);
                let toIndex = targetIndex;
                if (dragColumn.columnId !== "status" && targetIndex >= currentIdx) toIndex = targetIndex + 1;
                if (fromIndex < toIndex) toIndex--;
                withStatus.splice(toIndex, 0, moved);
                const newStatusIndex = withStatus.findIndex((c) => c.id === "status");
                if (dragColumn.columnId === "status") {
                  const gid = dragColumn.groupId || groupId;
                  if (gid) {
                    return {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === gid ? { ...g, statusIndex: newStatusIndex } : g,
                      ),
                    };
                  }
                  return sub;
                }
                return {
                  ...sub,
                  columns: withStatus.filter((c) => c.id !== "status"),
                  statusIndex: newStatusIndex,
                };
              }),
            }
          : sec,
      ),
  );
  setDragColumn(null);
  setDragColumnOver(null);
  };

  const handleGroupDrop = (
    sectionId: string,
    subId: string,
    targetIndex: number,
  ) => {
    if (!dragGroup) return;
    if (dragGroup.sectionId !== sectionId || dragGroup.subId !== subId) {
      setDragGroup(null);
      setDragGroupOver(null);
      return;
    }
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: (() => {
                        const arr = [...sub.groups];
                        const [moved] = arr.splice(dragGroup.index, 1);
                        let insertIndex = targetIndex;
                        if (dragGroup.index < targetIndex) insertIndex = targetIndex - 1;
                        arr.splice(insertIndex, 0, moved);
                        return arr;
                      })(),
                    }
                  : sub,
              ),
            }
          : sec,
      ),
    );
    setDragGroup(null);
    setDragGroupOver(null);
  };

  const handleSubTaskDrop = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    targetIndex: number,
  ) => {
    if (!dragSubTask) return;
    if (
      dragSubTask.sectionId !== sectionId ||
      dragSubTask.subId !== subId ||
      dragSubTask.groupId !== groupId ||
      dragSubTask.taskId !== taskId
    ) {
      setDragSubTask(null);
      setDragSubTaskOver(null);
      return;
    }
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              subsections: sec.subsections.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      groups: s.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              tasks: g.tasks.map((t) =>
                                t.id === taskId
                                  ? {
                                      ...t,
                                      subTasks: (() => {
                                        const arr = [...(t.subTasks || [])];
                                        const fromIndex = arr.findIndex(
                                          (st) => st.id === dragSubTask.subTaskId,
                                        );
                                        if (fromIndex === -1) return arr;
                                        const [moved] = arr.splice(fromIndex, 1);
                                        let insertIndex = targetIndex;
                                        if (fromIndex < targetIndex)
                                          insertIndex = targetIndex - 1;
                                        arr.splice(insertIndex, 0, moved);
                                        return arr;
                                      })(),
                                    }
                                  : t,
                              ),
                            }
                          : g,
                      ),
                    }
                  : s,
              ),
            }
          : sec,
      ),
    );
    setDragSubTask(null);
    setDragSubTaskOver(null);
  };

  const addColumn = (
    sectionId: string,
    subId: string,
    groupId: string,
    colType: "text" | "person" | "status" | "date",
  ) => {
    const title =
      colType === "person"
        ? "Person"
        : colType === "status"
        ? "Status"
        : colType === "date"
        ? "Date"
        : "New Column";
    const newId = `col-${Date.now()}`;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              columns: [...(g.columns || []), { id: newId, title, width: 150, type: colType }],
                              tasks: g.tasks.map((t) => ({
                                ...t,
                                extras: { ...t.extras, [newId]: "" },
                              })),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const saveColumnEdit = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
  ) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              columns: (g.columns || []).map((c) =>
                                c.id === columnId ? { ...c, title: editColumnTitle } : c,
                              ),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
    setEditingColumn(null);
  };

  const deleteColumn = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
  ) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              subsections: s.subsections.map((sub) =>
                sub.id === subId
                  ? {
                      ...sub,
                      groups: sub.groups.map((g) =>
                        g.id === groupId
                          ? {
                              ...g,
                              columns: (g.columns || []).filter((c) => c.id !== columnId),
                              tasks: g.tasks.map((t) => {
                                const { [columnId]: _r, ...rest } = t.extras;
                                return { ...t, extras: rest };
                              }),
                            }
                          : g,
                      ),
                    }
                  : sub,
              ),
            }
          : s,
      ),
    );
  };

  const addCustomLabel = (
    sectionId: string,
    opt: StatusOption,
  ) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? { ...sec, customStatusOptions: [...sec.customStatusOptions, opt] }
          : sec,
      ),
    );
  };

  const deleteLabel = (sectionId: string, label: string) => {
    if (defaultLabelSet.has(label)) return;
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              customStatusOptions: sec.customStatusOptions.filter(
                (opt) => opt.label !== label,
              ),
              subsections: sec.subsections.map((sub) => ({
                ...sub,
                groups: sub.groups.map((g) => ({
                  ...g,
                  tasks: g.tasks.map((task) => ({
                    ...task,
                    extras: (() => {
                      const newExtras = { ...task.extras };
                      Object.keys(newExtras).forEach((key) => {
                        const isStatusColumn =
                          key === "status" ||
                          sub.columns.some(
                            (c) => c.id === key && c.type === "status",
                          ) ||
                          (g.columns || []).some(
                            (c) => c.id === key && c.type === "status",
                          );
                        if (isStatusColumn && newExtras[key] === label)
                          newExtras[key] = "";
                      });
                      return newExtras;
                    })(),
                  })),
                })),
              })),
            }
          : sec,
      ),
    );
  };

  const keyFrom = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => `${sectionId}|${subId}|${groupId}|${taskId}`;

  const subTaskKeyFrom = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
    subTaskId: string,
  ) => `${sectionId}|${subId}|${groupId}|${taskId}|${subTaskId}`;

  const colKeyFrom = (
    sectionId: string,
    subId: string,
    groupId: string,
    columnId: string,
  ) => `${sectionId}|${subId}|${groupId}|${columnId}`;

  const toggleTaskSelection = (
    sectionId: string,
    subId: string,
    groupId: string,
    taskId: string,
  ) => {
    const key = keyFrom(sectionId, subId, groupId, taskId);
    setSelectedTasks((prev) => {
      const set = new Set(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return set;
    });
  };

  const toggleSelectAllInGroup = (
    sectionId: string,
    subId: string,
    groupId: string,
  ) => {
    setSelectedTasks((prev) => {
      const set = new Set(prev);
      const sec = sections.find((s) => s.id === sectionId);
      const group = sec?.subsections
        .find((s) => s.id === subId)?.groups.find((g) => g.id === groupId);
      if (!group) return set;
      const keys = group.tasks.map((t) =>
        keyFrom(sectionId, subId, groupId, t.id),
      );
      const allSelected = keys.every((k) => set.has(k));
      if (allSelected) {
        keys.forEach((k) => set.delete(k));
      } else {
        keys.forEach((k) => set.add(k));
      }
      return set;
    });
  };

  const deleteSelectedTasks = () => {
    if (selectedTasks.size === 0) return;
    setSections((prev) =>
      prev.map((sec) => ({
        ...sec,
        subsections: sec.subsections.map((sub) => ({
          ...sub,
          groups: sub.groups.map((g) => ({
            ...g,
            tasks: g.tasks.filter(
              (t) => !selectedTasks.has(keyFrom(sec.id, sub.id, g.id, t.id)),
            ),
          })),
        })),
      })),
    );
    setSelectedTasks(new Set());
  };

  const duplicateSelectedTasks = () => {
    if (selectedTasks.size === 0) return;
    setSections((prev) =>
      prev.map((sec) => ({
        ...sec,
        subsections: sec.subsections.map((sub) => ({
          ...sub,
          groups: sub.groups.map((g) => {
            const newTasks: Task[] = [];
            g.tasks.forEach((t) => {
              newTasks.push(t);
              if (selectedTasks.has(keyFrom(sec.id, sub.id, g.id, t.id))) {
                newTasks.push({
                  ...t,
                  id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                });
              }
            });
            return { ...g, tasks: newTasks };
          }),
        })),
      })),
    );
  };

  const openMoveDropdown = () => {
    const paths = Array.from(selectedTasks);
    if (paths.length === 0) return;
    const [sectionId, subId] = paths[0].split('|');
    if (!paths.every((p) => p.startsWith(`${sectionId}|${subId}|`))) {
      alert('Select tasks from a single subsection to move.');
      return;
    }
    const section = sections.find((s) => s.id === sectionId);
    const sub = section?.subsections.find((s) => s.id === subId);
    if (!sub || sub.groups.length === 0) return;
    setMoveContext({ sectionId, subId });
    setMoveTargetGroup(sub.groups[0].id);
    setMoveDropdownOpen(true);
  };

  const moveSelectedTasks = (choice: string) => {
    const paths = Array.from(selectedTasks);
    if (paths.length === 0) return;
    const [sectionId, subId] = paths[0].split('|');
    if (!paths.every((p) => p.startsWith(`${sectionId}|${subId}|`))) {
      alert('Select tasks from a single subsection to move.');
      return;
    }
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        return {
          ...sec,
          subsections: sec.subsections.map((s) => {
            if (s.id !== subId) return s;
            const tasksToMove: Task[] = [];
            const updatedGroups = s.groups.map((g) => {
              const remaining = g.tasks.filter((t) => {
                const keep = !selectedTasks.has(keyFrom(sec.id, s.id, g.id, t.id));
                if (!keep) tasksToMove.push(t);
                return keep;
              });
              return { ...g, tasks: remaining };
            });
            return {
              ...s,
              groups: updatedGroups.map((g) =>
                g.id === choice
                  ? { ...g, tasks: [...g.tasks, ...tasksToMove] }
                  : g,
              ),
            };
          }),
        };
      }),
    );
    setSelectedTasks(new Set());
  };

  const getStatusColor = (sectionId: string, label: string) => {
    const opts = getSectionStatusOptions(sections, sectionId);
    const found = opts.find((o) => o.label === label);
    return found ? found.color : "#dedede";
  };

  return (
    <div
      ref={containerRef}
      className="relative flex mt-10 w-full overflow-hidden"
      style={{ backgroundColor: "#fdfdfd", minHeight: "100vh", width: "100%" }}
    >
      <nav
        className="pt-4 px-4 pb-4 text-sm flex-shrink-0"
        style={{ width: `${sidebarWidth}%`, backgroundColor: "#f5f6fb" }}
      >
        <button
          onClick={onBack}
          className="mb-4 px-2 py-1 text-xs rounded bg-gray-200"
        >
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold">Contents</h2>
          <button
            onClick={addSection}
            className="text-xl leading-none text-blue-600 hover:text-blue-800"
          >
            +
          </button>
        </div>
        <ul className="space-y-2">
          {sections.map((section, idx) => (
            <li key={section.id} className="relative group flex items-start">
              <div className="flex flex-col mr-1 opacity-0 group-hover:opacity-100">
                <button
                  onClick={() => moveSectionUp(idx)}
                  disabled={idx === 0}
                  className="text-xs leading-none disabled:opacity-50 text-gray-400"
                >
                  ▲
                </button>
                <button
                  onClick={() => moveSectionDown(idx)}
                  disabled={idx === sections.length - 1}
                  className="text-xs leading-none disabled:opacity-50 text-gray-400"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
              {editingId === section.id ? (
                <div className="flex items-center space-x-2">
                  <input
                    autoFocus
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="flex-1 px-4 py-2 rounded border"
                  />
                  <input
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                  />
                  <button
                    onClick={() => saveEdit(section.id)}
                    className="px-2 py-1 border rounded bg-blue-600 text-white"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-4 py-2 rounded text-white"
                  style={{ backgroundColor: section.color }}
                >
                  {section.title}
                </button>
              )}
              {editingId !== section.id && (
                <div
                  className={`absolute right-2 top-1/2 -translate-y-1/2 flex space-x-2 ${openSections.has(section.id) ? "hidden" : "opacity-0 group-hover:opacity-100"}`}
                >
                  <button
                    onClick={() => {
                      setEditingId(section.id);
                      setEditTitle(section.title);
                      setEditColor(section.color);
                    }}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => confirmSectionDelete(section.id)}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    🗑️
                  </button>
                </div>
              )}
              {openSections.has(section.id) && (
                <ul className="ml-4 mt-2 space-y-1">
                  {section.subsections.map((sub, subIdx) => (
                    <li key={sub.id} className="relative group/sub flex items-start">
                      <div className="flex flex-col mr-1 opacity-0 group-hover/sub:opacity-100">
                        <button
                          onClick={() => moveSubUp(section.id, subIdx)}
                          disabled={subIdx === 0}
                          className="text-xs leading-none disabled:opacity-50 text-gray-400"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => moveSubDown(section.id, subIdx)}
                          disabled={subIdx === section.subsections.length - 1}
                          className="text-xs leading-none disabled:opacity-50 text-gray-400"
                        >
                          ▼
                        </button>
                      </div>
                      <div className="flex-1">
                      {editingSub &&
                      editingSub.sectionId === section.id &&
                      editingSub.subId === sub.id ? (
                        <input
                          autoFocus
                          value={editSubTitle}
                          onChange={(e) => setEditSubTitle(e.target.value)}
                          onBlur={() => saveSubEdit(section.id, sub.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              saveSubEdit(section.id, sub.id);
                          }}
                          className="w-full px-2 py-1 rounded border"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setActiveSection(section.id);
                            setActiveSub({
                              sectionId: section.id,
                              subId: sub.id,
                            });
                          }}
                          className="w-full text-left px-2 py-1 rounded bg-gray-200"
                        >
                          {sub.title}
                        </button>
                      )}
                      </div>
                      {!(
                        editingSub &&
                        editingSub.sectionId === section.id &&
                        editingSub.subId === sub.id
                      ) && (
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex space-x-1 opacity-0 group-hover/sub:opacity-100">
                          <button
                            onClick={() => {
                              setEditingSub({
                                sectionId: section.id,
                                subId: sub.id,
                              });
                              setEditSubTitle(sub.title);
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => confirmSubDelete(section.id, sub.id)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            🗑️
                          </button>
                        </div>
                )}
            </li>
            ))}
                  <li>
                    <button
                      onClick={() => addSubSection(section.id)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Sub-section
                    </button>
                  </li>
                </ul>
              )}
              </div>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex-shrink-0 w-1 bg-gray-300 cursor-col-resize" onMouseDown={startResize}></div>
      <div className="p-4 space-y-8 flex-1 min-w-0 overflow-x-auto" style={{ backgroundColor: "#fdfdfd" }}>
        {activeSub
          ? (() => {
              const section = sections.find(
                (s) => s.id === activeSub.sectionId,
              );
              const sub = section?.subsections.find(
                (ss) => ss.id === activeSub.subId,
              );
              if (!section || !sub) return null;
              return (
                <section key={sub.id} className="border-b pb-4">
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: section.color }}
                  >
                    {section.title} - {sub.title}
                  </h3>
                  {selectedTasks.size > 0 && (
                    <div className="mb-2 space-x-1 text-sm">
                      <button
                        onClick={deleteSelectedTasks}
                        className="px-2 py-0.5 border rounded bg-red-600 text-white"
                      >
                        Delete
                      </button>
                      <button
                        onClick={duplicateSelectedTasks}
                        className="px-2 py-0.5 border rounded bg-gray-600 text-white"
                      >
                        Duplicate
                      </button>
                      {moveDropdownOpen ? (
                        <>
                          <select
                            value={moveTargetGroup}
                            onChange={(e) => setMoveTargetGroup(e.target.value)}
                            className="border rounded px-2 py-0.5"
                          >
                            {moveContext &&
                              sections
                                .find((s) => s.id === moveContext.sectionId)
                                ?.subsections.find((s) => s.id === moveContext.subId)
                                ?.groups.map((g) => (
                                  <option key={g.id} value={g.id}>
                                    {g.title}
                                  </option>
                                ))}
                          </select>
                          <button
                            onClick={() => {
                              moveSelectedTasks(moveTargetGroup);
                              setMoveDropdownOpen(false);
                            }}
                            className="px-2 py-0.5 border rounded bg-blue-600 text-white"
                          >
                            Move
                          </button>
                          <button
                            onClick={() => setMoveDropdownOpen(false)}
                            className="px-2 py-0.5 border rounded"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={openMoveDropdown}
                          className="px-2 py-0.5 border rounded bg-blue-600 text-white"
                        >
                          Move To
                        </button>
                      )}
                    </div>
                  )}
                  {sub.groups.map((group, gIdx) => {
                    let statusIdx = group.statusIndex ?? sub.statusIndex ?? 0;
                    const baseCols = [...sub.columns, ...(group.columns || [])];
                    if (statusIdx > baseCols.length) statusIdx = baseCols.length;
                    const allCols = [...baseCols];
                    allCols.splice(statusIdx, 0, {
                      id: "status",
                      title: "Status",
                      width: group.statusWidth ?? sub.statusWidth,
                      type: "status",
                    });
                    const secondColWidth =
                      allCols[0]?.width ?? group.statusWidth ?? sub.statusWidth;
                    return (
                    <div key={group.id} className="mb-4 shadow">
                      {editingGroup &&
                      editingGroup.sectionId === section.id &&
                      editingGroup.subId === sub.id &&
                      editingGroup.groupId === group.id ? (
                        <div className="flex items-center space-x-2 mb-2">
                          <input
                            autoFocus
                            value={editGroupTitle}
                            onChange={(e) => setEditGroupTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                saveGroupEdit(section.id, sub.id, group.id);
                            }}
                            className="flex-1 border rounded p-1"
                          />
                          <input
                            type="color"
                            value={editGroupColor}
                            onChange={(e) => setEditGroupColor(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                saveGroupEdit(section.id, sub.id, group.id);
                            }}
                          />
                          <button
                            onClick={() => saveGroupEdit(section.id, sub.id, group.id)}
                            className="px-2 py-1 border rounded bg-blue-600 text-white"
                          >
                            Save
                          </button>
                        </div>
                      ) : null}
                      <div className="w-full overflow-y-auto" style={{ maxHeight: "60vh" }}>
                        <div className="overflow-x-auto" style={{ display: "block", maxWidth: "100%" }}>
                          <table className="min-w-max table-fixed text-sm border bg-white rounded-lg overflow-hidden">
                          <thead className="sticky top-0 bg-white z-10">
                            <tr
                              draggable
                              onDragStart={() =>
                                setDragGroup({
                                  sectionId: section.id,
                                  subId: sub.id,
                                  index: gIdx,
                                })
                              }
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragGroupOver({
                                  sectionId: section.id,
                                  subId: sub.id,
                                  index: gIdx,
                                });
                              }}
                              onDrop={() => handleGroupDrop(section.id, sub.id, gIdx)}
                              onDragLeave={() => setDragGroupOver(null)}
                              onDragEnd={() => {
                                setDragGroup(null);
                                setDragGroupOver(null);
                              }}
                              className={`${
                                dragGroup &&
                                dragGroup.sectionId === section.id &&
                                dragGroup.subId === sub.id &&
                                dragGroup.index === gIdx
                                  ? "opacity-50"
                                  : ""
                              } ${
                                dragGroupOver &&
                                dragGroupOver.sectionId === section.id &&
                                dragGroupOver.subId === sub.id &&
                                dragGroupOver.index === gIdx
                                  ? "bg-blue-100"
                                  : ""
                              }`}
                            >
                              <th
                                colSpan={allCols.length + 4}
                                className="border px-2 py-1"
                                style={{
                                  backgroundColor: group.color,
                                  color: isColorDark(group.color) ? "#ffffff" : "#000000",
                                }}
                              >
                                <div className="flex items-center justify-between group">
                                  <button
                                    onClick={() => toggleGroup(group.id)}
                                    className="font-semibold flex items-center space-x-1"
                                  >
                                    <span>{group.title}</span>
                                    {openGroups.has(group.id) ? (
                                      <EyeOffIcon />
                                    ) : (
                                      <EyeIcon />
                                    )}
                                  </button>
                                  <div className="space-x-2 opacity-0 group-hover:opacity-100">
                                    <button
                                      onClick={() => {
                                        setEditingGroup({
                                          sectionId: section.id,
                                          subId: sub.id,
                                          groupId: group.id,
                                        });
                                        setEditGroupTitle(group.title);
                                        setEditGroupColor(group.color);
                                      }}
                                      className="text-gray-600 hover:text-gray-800"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={() =>
                                        confirmGroupDelete(
                                          section.id,
                                          sub.id,
                                          group.id,
                                        )
                                      }
                                      className="text-gray-600 hover:text-gray-800"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </th>
                            </tr>
                            <tr className={!openGroups.has(group.id) ? "invisible" : ""}>
                                {(() => {
                                  const keys = group.tasks.map((t) =>
                                    keyFrom(section.id, sub.id, group.id, t.id),
                                  );
                                  const allSelected =
                                    keys.length > 0 && keys.every((k) => selectedTasks.has(k));
                                  const someSelected = keys.some((k) => selectedTasks.has(k));
                                  return (
                                    <th
                                      className="border px-2 py-1 w-4 text-center"
                                      style={{
                                        backgroundColor: group.color,
                                        color: isColorDark(group.color) ? "#ffffff" : "#000000",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        ref={(el) => {
                                          if (el) el.indeterminate = !allSelected && someSelected;
                                        }}
                                        checked={allSelected}
                                        onChange={() =>
                                          toggleSelectAllInGroup(section.id, sub.id, group.id)
                                        }
                                      />
                                    </th>
                                  );
                                })()}
                                <th
                                  ref={(el) => {
                                    headerRefs.current[
                                      colKeyFrom(section.id, sub.id, group.id, "task")
                                    ] = el;
                                  }}
                                  className="border px-2 py-1 relative min-w-0"
                                  style={{ width: group.taskWidth ?? sub.taskWidth }}
                                >
                                  <div className="overflow-hidden text-ellipsis whitespace-nowrap" title="Task">
                                    Task
                                  </div>
                                  <div
                                    onMouseDown={(e) =>
                                      startColumnResize(section.id, sub.id, "task", e, group.id)
                                    }
                                    onDragStart={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 h-full w-2 cursor-col-resize"
                                  />
                                </th>
                                {allCols.map((col, cIdx) => {
                                  const isGroupCol = (group.columns || []).some((c) => c.id === col.id);
                                  const combined = [...sub.columns, ...(group.columns || [])];
                                  const idx = col.id === "status"
                                    ? group.statusIndex ?? sub.statusIndex ?? combined.length
                                    : combined.findIndex((c) => c.id === col.id);
                                  const gId = col.id === "status" ? group.id : isGroupCol ? group.id : "";
                                  return (
                                  <th
                                    ref={(el) => {
                                      headerRefs.current[
                                        colKeyFrom(section.id, sub.id, gId, col.id)
                                      ] = el;
                                    }}
                                    key={col.id}
                                    draggable
                                    onDragStart={() =>
                                      setDragColumn({
                                        sectionId: section.id,
                                        subId: sub.id,
                                        groupId: gId,
                                        columnId: col.id,
                                        index: idx,
                                      })
                                    }
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      setDragColumnOver({
                                        sectionId: section.id,
                                        subId: sub.id,
                                        groupId: gId,
                                        index: idx,
                                      });
                                    }}
                                    onDrop={() => handleColumnDrop(section.id, sub.id, gId, idx)}
                                    onDragLeave={() => setDragColumnOver(null)}
                                    onDragEnd={() => {
                                      setDragColumn(null);
                                      setDragColumnOver(null);
                                    }}
                                    className={`border px-2 py-1 relative min-w-0 group ${
                                      dragColumn && dragColumn.columnId === col.id
                                        ? "opacity-50"
                                        : ""
                                    } ${
                                      dragColumnOver &&
                                      dragColumnOver.sectionId === section.id &&
                                      dragColumnOver.subId === sub.id &&
                                      dragColumnOver.groupId === gId &&
                                      dragColumnOver.index === idx
                                        ? "bg-blue-100"
                                        : ""
                                    }`}
                                    style={{ width: col.width }}
                                  >
                                    {col.id === "status" ? (
                                      <>
                                        <div className="flex items-center">
                                          <button
                                            onClick={() =>
                                              toggleSort(section.id, sub.id, group.id, col.id, col.type)
                                            }
                                            className="mr-1 opacity-0 group-hover:opacity-100 text-gray-400"
                                          >
                                            {sortState[colKeyFrom(section.id, sub.id, group.id, col.id)] ===
                                            "desc"
                                              ? "▲"
                                              : "▼"}
                                          </button>
                                          <span>Status</span>
                                        </div>
                                        <div
                                          onMouseDown={(e) =>
                                            startColumnResize(section.id, sub.id, "status", e, group.id)
                                          }
                                          onDragStart={(e) => e.stopPropagation()}
                                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize"
                                        />
                                      </>
                                    ) : (
                                      <>
                                        {editingColumn &&
                                        editingColumn.sectionId === section.id &&
                                        editingColumn.subId === sub.id &&
                                        editingColumn.groupId === group.id &&
                                        editingColumn.columnId === col.id ? (
                                          <input
                                            autoFocus
                                            value={editColumnTitle}
                                            onChange={(e) => setEditColumnTitle(e.target.value)}
                                            onBlur={() =>
                                              saveColumnEdit(section.id, sub.id, group.id, col.id)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") {
                                                e.preventDefault();
                                                saveColumnEdit(section.id, sub.id, group.id, col.id);
                                              }
                                            }}
                                            onFocus={(e) => e.currentTarget.select()}
                                            className="w-full border rounded p-1"
                                          />
                                        ) : (
                                          <div className="flex items-center">
                                            {(["date", "person", "status"].includes(col.type)) && (
                                              <button
                                                onClick={() =>
                                                  toggleSort(section.id, sub.id, group.id, col.id, col.type)
                                                }
                                                className="mr-1 opacity-0 group-hover:opacity-100 text-gray-400"
                                              >
                                                {sortState[colKeyFrom(section.id, sub.id, group.id, col.id)] ===
                                                "desc"
                                                  ? "▲"
                                                  : "▼"}
                                              </button>
                                            )}
                                            <span
                                              onDoubleClick={() => {
                                                setEditingColumn({
                                                  sectionId: section.id,
                                                  subId: sub.id,
                                                  groupId: group.id,
                                                  columnId: col.id,
                                                });
                                                setEditColumnTitle(col.title);
                                              }}
                                              className="cursor-pointer block overflow-hidden text-ellipsis whitespace-nowrap"
                                              title={col.title}
                                            >
                                              {col.title}
                                            </span>
                                          </div>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            confirmColumnDelete(
                                              section.id,
                                              sub.id,
                                              group.id,
                                              col.id,
                                            );
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onDragStart={(e) => e.stopPropagation()}
                                          className="ml-1 text-red-600 absolute top-1 right-1 opacity-0 group-hover:opacity-100 z-10"
                                        >
                                          🗑️
                                        </button>
                                        <div
                                          onMouseDown={(e) =>
                                            startColumnResize(section.id, sub.id, col.id, e, group.id)
                                          }
                                          onDragStart={(e) => e.stopPropagation()}
                                          className="absolute top-0 right-0 h-full w-2 cursor-col-resize"
                                        />
                                      </>
                                    )}
                                  </th>
                                );
                              })}
                                <th
                                  className={`border px-2 py-1 min-w-0 ${
                                    dragColumnOver &&
                                    dragColumnOver.sectionId === section.id &&
                                    dragColumnOver.subId === sub.id &&
                                    dragColumnOver.index ===
                                      sub.columns.length + (group.columns || []).length
                                      ? "bg-blue-100"
                                      : ""
                                  }`}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragColumnOver({
                                      sectionId: section.id,
                                      subId: sub.id,
                                      groupId: group.id,
                                      index: sub.columns.length + (group.columns || []).length,
                                    });
                                  }}
                                  onDrop={() =>
                                    handleColumnDrop(
                                      section.id,
                                      sub.id,
                                      group.id,
                                      sub.columns.length + (group.columns || []).length,
                                    )
                                  }
                                  onDragLeave={() => setDragColumnOver(null)}
                                >
                                  <button
                                    onClick={() => {
                                      setNewColumnType("text");
                                      setColumnPopup({
                                        sectionId: section.id,
                                        subId: sub.id,
                                        groupId: group.id,
                                      });
                                    }}
                                  >
                                    +
                                  </button>
                                </th>
                              </tr>
                          </thead>
                          {openGroups.has(group.id) && (
                            <tbody
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver({
                                  sectionId: section.id,
                                  subId: sub.id,
                                  groupId: group.id,
                                  index: group.tasks.length,
                                });
                              }}
                              onDrop={(e) => {
                                e.stopPropagation();
                                handleTaskDrop(
                                  section.id,
                                  sub.id,
                                  group.id,
                                  group.tasks.length,
                                );
                              }}
                              onDragLeave={() => setDragOver(null)}
                            >
                              {group.tasks.map((task, idx) => {
                                const isEditingRow =
                                  (editingTask &&
                                    editingTask.sectionId === section.id &&
                                    editingTask.subId === sub.id &&
                                    editingTask.groupId === group.id &&
                                    editingTask.taskId === task.id) ||
                                  (editingExtra &&
                                    editingExtra.sectionId === section.id &&
                                    editingExtra.subId === sub.id &&
                                    editingExtra.groupId === group.id &&
                                    editingExtra.taskId === task.id);
                                return (
                                <React.Fragment key={keyFrom(section.id, sub.id, group.id, task.id)}>
                                <tr
                                  draggable={!isEditingRow}
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    setDragTask({
                                      sectionId: section.id,
                                      subId: sub.id,
                                      groupId: group.id,
                                      taskId: task.id,
                                      index: idx,
                                    });
                                  }}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragOver({
                                      sectionId: section.id,
                                      subId: sub.id,
                                      groupId: group.id,
                                      index: idx,
                                    });
                                  }}
                                  onDrop={(e) => {
                                    e.stopPropagation();
                                    handleTaskDrop(
                                      section.id,
                                      sub.id,
                                      group.id,
                                      idx,
                                    );
                                  }}
                                  onDragLeave={() => setDragOver(null)}
                                  className={`${
                                    dragTask && dragTask.taskId === task.id
                                      ? "opacity-50"
                                      : ""
                                  } ${
                                    dragOver &&
                                    dragOver.groupId === group.id &&
                                    dragOver.index === idx
                                      ? "bg-blue-100"
                                      : ""
                                  }`}
                                >
                                  <td
                                    className="border px-2 py-1 text-center cursor-move"
                                    style={{
                                      backgroundColor: group.color,
                                      color: isColorDark(group.color) ? "#ffffff" : "#000000",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedTasks.has(
                                        keyFrom(section.id, sub.id, group.id, task.id),
                                      )}
                                      onChange={() =>
                                        toggleTaskSelection(
                                          section.id,
                                          sub.id,
                                          group.id,
                                          task.id,
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="border px-2 py-1 min-w-0" style={{ width: group.taskWidth ?? sub.taskWidth }}>
                                    <div className="flex items-center justify-between space-x-1 group">
                                      {editingTask &&
                                      editingTask.sectionId === section.id &&
                                      editingTask.subId === sub.id &&
                                      editingTask.groupId === group.id &&
                                      editingTask.taskId === task.id ? (
                                      <input
                                          autoFocus
                                          value={task.text}
                                          onChange={(e) =>
                                            updateTaskText(
                                              section.id,
                                              sub.id,
                                              group.id,
                                              task.id,
                                              e.target.value,
                                            )
                                          }
                                          onPaste={(e) =>
                                            handleLinkPaste(e, (val) =>
                                              updateTaskText(
                                                section.id,
                                                sub.id,
                                                group.id,
                                                task.id,
                                                val,
                                              ),
                                            )
                                          }
                                          onBlur={() => setEditingTask(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              setEditingTask(null);
                                            }
                                          }}
                                          className="flex-grow border rounded p-1"
                                        />
                                      ) : (
                                        <>
                                          <button
                                            onClick={() =>
                                              setEditingTask({
                                                sectionId: section.id,
                                                subId: sub.id,
                                                groupId: group.id,
                                                taskId: task.id,
                                              })
                                            }
                                            className="flex-grow text-left p-1"
                                            title={task.text || "Edit"}
                                          >
                                            <ResizableTruncatableCell
                                              text={task.text || ""}
                                              renderText={renderTextWithLinks}
                                              placeholder={<span className="text-gray-400">Edit</span>}
                                            />
                                          </button>
                                          <button
                                            onClick={() =>
                                              setEditingTask({
                                                sectionId: section.id,
                                                subId: sub.id,
                                                groupId: group.id,
                                                taskId: task.id,
                                              })
                                            }
                                            className="text-gray-600 hover:text-gray-800 opacity-0 group-hover:opacity-100"
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            onClick={() => toggleSubTaskOpen(task.id)}
                                            className={`relative text-gray-600 hover:text-gray-800 ${
                                              task.subTasks && task.subTasks.length > 0
                                                ? ""
                                                : "opacity-0 group-hover:opacity-100"
                                            }`}
                                          >
                                            {openSubTasks.has(task.id) ? "🌿" : "🌱"}
                                            {task.subTasks && task.subTasks.length > 0 && (
                                              <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full px-1">
                                                {task.subTasks.length}
                                              </span>
                                            )}
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={() => {
                                          setCommentPopup({
                                            sectionId: section.id,
                                            subId: sub.id,
                                            groupId: group.id,
                                            taskId: task.id,
                                          });
                                          setNewCommentText("");
                                          setNewCommentFiles(null);
                                        }}
                                        className="text-gray-600 hover:text-gray-800 relative"
                                      >
                                        <ChatIcon />
                                        {task.comments && task.comments.length > 0 && (
                                          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full px-1">
                                            {task.comments.length}
                                          </span>
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                  {allCols.map((col) => (
                                    <td
                                      key={col.id}
                                      className="border px-2 py-1 min-w-0"
                                      style={{ width: col.width }}
                                    >
                                      {col.type === "person" ? (
                                        <button
                                          onClick={() => {
                                            setPersonPopup({
                                              sectionId: section.id,
                                              subId: sub.id,
                                              groupId: group.id,
                                              taskId: task.id,
                                              columnId: col.id,
                                            });
                                          }}
                                          className="w-full flex justify-center"
                                        >
                                          {(() => {
                                            const person = people.find(
                                              (p) => p.id === task.extras[col.id],
                                            );
                                            return person ? (
                                              <span
                                                className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-black"
                                                style={{ backgroundColor: person.color }}
                                                title={person.name}
                                              >
                                                {getInitials(person.name)}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">+</span>
                                            );
                                          })()}
                                        </button>
                                      ) : col.type === "status" ? (
                                        <select
                                          value={task.extras[col.id] || ""}
                                          onChange={(e) =>
                                            handleStatusChange(
                                              section.id,
                                              sub.id,
                                              group.id,
                                              task.id,
                                              col.id,
                                              e.target.value,
                                            )
                                          }
                                          className="w-full border rounded p-1"
                                          style={{
                                            backgroundColor: getStatusColor(
                                              section.id,
                                              task.extras[col.id],
                                            ),
                                            color: task.extras[col.id]
                                              ? "white"
                                              : "black",
                                          }}
                                        >
                                          {getSectionStatusOptions(
                                            sections,
                                            section.id,
                                          ).map((opt, idx) => (
                                            <option
                                              key={idx}
                                              value={opt.label}
                                              style={{
                                                backgroundColor: opt.color,
                                                color: opt.label ? "white" : "black",
                                              }}
                                            >
                                              {opt.label === "" ? "" : opt.label}
                                            </option>
                                          ))}
                                          <option
                                            value={ADD_LABEL_VALUE}
                                            style={{
                                              backgroundColor: DEFAULT_BLANK_LABEL_COLOR,
                                              color: isColorDark(DEFAULT_BLANK_LABEL_COLOR)
                                                ? "white"
                                                : "black",
                                            }}
                                          >
                                            + Add Label
                                          </option>
                                        </select>
                                      ) : col.type === "date" ? (
                                        editingExtra &&
                                        editingExtra.sectionId === section.id &&
                                        editingExtra.subId === sub.id &&
                                        editingExtra.groupId === group.id &&
                                        editingExtra.taskId === task.id &&
                                        editingExtra.columnId === col.id ? (
                                          <input
                                            type="date"
                                            autoFocus
                                            value={toInputDate(task.extras[col.id] || "")}
                                            onChange={(e) =>
                                              updateTaskExtra(
                                                section.id,
                                                sub.id,
                                                group.id,
                                                task.id,
                                                col.id,
                                                fromInputDate(e.target.value),
                                              )
                                            }
                                            onBlur={() => setEditingExtra(null)}
                                            className="w-full border rounded p-1"
                                          />
                                        ) : (
                                          <button
                                            onClick={() =>
                                              setEditingExtra({
                                                sectionId: section.id,
                                                subId: sub.id,
                                                groupId: group.id,
                                                taskId: task.id,
                                                columnId: col.id,
                                              })
                                            }
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onDragStart={(e) => e.stopPropagation()}
                                            draggable={false}
                                            className="w-full text-left p-1"
                                            title={task.extras[col.id] || "Edit"}
                                          >
                                            <ResizableTruncatableCell
                                              text={task.extras[col.id] || ""}
                                              renderText={renderTextWithLinks}
                                              placeholder={<span className="text-gray-400">Edit</span>}
                                            />
                                          </button>
                                        )
                                      ) : (
                                        editingExtra &&
                                        editingExtra.sectionId === section.id &&
                                        editingExtra.subId === sub.id &&
                                        editingExtra.groupId === group.id &&
                                        editingExtra.taskId === task.id &&
                                        editingExtra.columnId === col.id ? (
                                          <input
                                            autoFocus
                                            value={task.extras[col.id] || ""}
                                            onChange={(e) =>
                                              updateTaskExtra(
                                                section.id,
                                                sub.id,
                                                group.id,
                                                task.id,
                                                col.id,
                                                e.target.value,
                                              )
                                            }
                                            onPaste={(e) =>
                                              handleLinkPaste(e, (val) =>
                                                updateTaskExtra(
                                                  section.id,
                                                  sub.id,
                                                  group.id,
                                                  task.id,
                                                  col.id,
                                                  val,
                                                ),
                                              )
                                            }
                                            onBlur={() => setEditingExtra(null)}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") setEditingExtra(null);
                                            }}
                                            className="w-full border rounded p-1"
                                          />
                                        ) : (
                                            <button
                                              onClick={() =>
                                                setEditingExtra({
                                                  sectionId: section.id,
                                                  subId: sub.id,
                                                  groupId: group.id,
                                                  taskId: task.id,
                                                  columnId: col.id,
                                                })
                                              }
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onDragStart={(e) => e.stopPropagation()}
                                              draggable={false}
                                              className="w-full text-left p-1"
                                            >
                                              <ResizableTruncatableCell
                                                text={task.extras[col.id] || ""}
                                                renderText={renderTextWithLinks}
                                                placeholder={<span className="text-gray-400">Edit</span>}
                                              />
                                            </button>
                                          )
                                        )}
                                      </td>
                                  ))}
                                  <td className="border px-2 py-1"></td>
                                </tr>
                                {openSubTasks.has(task.id) && (
                                  <tr className="bg-gray-50">
                                    <td colSpan={allCols.length + 3} className="border px-2 py-1">
                                      <div className="mb-2 space-x-1 text-sm">
                                        {Array.from(selectedSubTasks).some((k) =>
                                          k.startsWith(
                                            `${section.id}|${sub.id}|${group.id}|${task.id}|`,
                                          ),
                                        ) && (
                                          <button
                                            onClick={deleteSelectedSubTasks}
                                            className="px-2 py-0.5 border rounded bg-red-600 text-white"
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                      <ul
                                        className="ml-6 space-y-1 divide-y divide-gray-200"
                                        onDragOver={(e) => {
                                          e.preventDefault();
                                          setDragSubTaskOver({
                                            sectionId: section.id,
                                            subId: sub.id,
                                            groupId: group.id,
                                            taskId: task.id,
                                            index: (task.subTasks || []).length,
                                          });
                                        }}
                                        onDrop={() =>
                                          handleSubTaskDrop(
                                            section.id,
                                            sub.id,
                                            group.id,
                                            task.id,
                                            (task.subTasks || []).length,
                                          )
                                        }
                                        onDragLeave={() => setDragSubTaskOver(null)}
                                      >
                                        {(task.subTasks || []).map((st, stIdx) => (
                                          <li
                                            key={st.id}
                                            className={`flex items-center space-x-2 py-1 ${
                                              dragSubTask && dragSubTask.subTaskId === st.id
                                                ? "opacity-50"
                                                : ""
                                            } ${
                                              dragSubTaskOver &&
                                              dragSubTaskOver.taskId === task.id &&
                                              dragSubTaskOver.index === stIdx
                                                ? "bg-blue-100"
                                                : ""
                                            }`}
                                            draggable={
                                              !(
                                                editingSubTask &&
                                                editingSubTask.sectionId === section.id &&
                                                editingSubTask.subId === sub.id &&
                                                editingSubTask.groupId === group.id &&
                                                editingSubTask.taskId === task.id &&
                                                editingSubTask.subTaskId === st.id
                                              )
                                            }
                                            onDragStart={() =>
                                              setDragSubTask({
                                                sectionId: section.id,
                                                subId: sub.id,
                                                groupId: group.id,
                                                taskId: task.id,
                                                subTaskId: st.id,
                                                index: stIdx,
                                              })
                                            }
                                            onDragOver={(e) => {
                                              e.preventDefault();
                                              setDragSubTaskOver({
                                                sectionId: section.id,
                                                subId: sub.id,
                                                groupId: group.id,
                                                taskId: task.id,
                                                index: stIdx,
                                              });
                                            }}
                                            onDrop={() =>
                                              handleSubTaskDrop(
                                                section.id,
                                                sub.id,
                                                group.id,
                                                task.id,
                                                stIdx,
                                              )
                                            }
                                            onDragLeave={() => setDragSubTaskOver(null)}
                                            onDragEnd={() => {
                                              setDragSubTask(null);
                                              setDragSubTaskOver(null);
                                            }}
                                          >
                                            <div
                                              className="flex items-center space-x-2"
                                              style={{ width: group.taskWidth ?? sub.taskWidth }}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={!!st.done}
                                                onChange={() => {
                                                  const newDone = !st.done;
                                                toggleSubTaskDone(
                                                  section.id,
                                                  sub.id,
                                                  group.id,
                                                  task.id,
                                                  st.id,
                                                );
                                                toggleSubTaskSelection(
                                                  section.id,
                                                  sub.id,
                                                  group.id,
                                                  task.id,
                                                  st.id,
                                                  newDone,
                                                );
                                              }}
                                            />
                                            {editingSubTask &&
                                            editingSubTask.sectionId === section.id &&
                                            editingSubTask.subId === sub.id &&
                                            editingSubTask.groupId === group.id &&
                                            editingSubTask.taskId === task.id &&
                                            editingSubTask.subTaskId === st.id ? (
                                              <input
                                                autoFocus
                                                value={st.text}
                                                onChange={(e) =>
                                                  updateSubTaskText(
                                                    section.id,
                                                    sub.id,
                                                    group.id,
                                                    task.id,
                                                    st.id,
                                                    e.target.value,
                                                  )
                                                }
                                                onBlur={() => setEditingSubTask(null)}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter") setEditingSubTask(null);
                                                }}
                                                className="flex-grow border rounded p-1"
                                              />
                                            ) : (
                                              <button
                                                onClick={() =>
                                                  setEditingSubTask({
                                                    sectionId: section.id,
                                                    subId: sub.id,
                                                    groupId: group.id,
                                                    taskId: task.id,
                                                    subTaskId: st.id,
                                                  })
                                                }
                                                className="flex-grow text-left"
                                              >
                                                {st.text || <span className="text-gray-400">Edit</span>}
                                              </button>
                                            )}
                                            </div>
                                            <select
                                              value={st.status || ""}
                                              onChange={(e) =>
                                                updateSubTaskStatus(
                                                  section.id,
                                                  sub.id,
                                                  group.id,
                                                  task.id,
                                                  st.id,
                                                  e.target.value,
                                                )
                                              }
                                              className="border rounded p-1 text-sm w-full"
                                              style={{
                                                width: secondColWidth,
                                                backgroundColor: getStatusColor(section.id, st.status || ""),
                                                color: st.status ? "white" : "black",
                                              }}
                                            >
                                              {getSectionStatusOptions(sections, section.id).map((opt, idx) => (
                                                <option
                                                  key={idx}
                                                  value={opt.label}
                                                  style={{ backgroundColor: opt.color, color: opt.label ? "white" : "black" }}
                                                >
                                                  {opt.label === "" ? "" : opt.label}
                                                </option>
                                              ))}
                                              <option
                                                value={ADD_LABEL_VALUE}
                                                style={{
                                                  backgroundColor: DEFAULT_BLANK_LABEL_COLOR,
                                                  color: isColorDark(DEFAULT_BLANK_LABEL_COLOR) ? "white" : "black",
                                                }}
                                              >
                                                + Add Label
                                              </option>
                                            </select>
                                          </li>
                                        ))}
                                        <li>
                                          <button
                                            onClick={() =>
                                              addSubTask(section.id, sub.id, group.id, task.id)
                                            }
                                            className="text-sm text-blue-600 hover:text-blue-800"
                                          >
                                            + Add Sub-task
                                          </button>
                                        </li>
                                      </ul>
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                                );
                              })}
                            </tbody>
                          )}
                          </table>
                          {openGroups.has(group.id) && (
                            <button
                              onClick={() =>
                                addTask(section.id, sub.id, group.id)
                              }
                              className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              + Add Task
                            </button>
                          )}
                            </div>
                        </div>
                        </div>
                      );
                    })}
                  <div
                    className={`h-2 ${
                      dragGroupOver &&
                      dragGroupOver.sectionId === section.id &&
                      dragGroupOver.subId === sub.id &&
                      dragGroupOver.index === sub.groups.length
                        ? 'bg-blue-100'
                        : ''
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragGroupOver({
                        sectionId: section.id,
                        subId: sub.id,
                        index: sub.groups.length,
                      });
                    }}
                    onDrop={() => handleGroupDrop(section.id, sub.id, sub.groups.length)}
                    onDragLeave={() => setDragGroupOver(null)}
                  />
                  <button
                    onClick={() => addGroup(section.id, sub.id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Task Set
                  </button>
                </section>
              );
            })()
          : sections
              .filter((section) => section.id === activeSection)
              .map((section) => (
                <section
                  id={section.id}
                  key={section.id}
                  className="border-b pb-4"
                >
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: section.color }}
                  >
                    {section.title}
                  </h3>
                  <div className="space-y-2">
                    {section.subsections.map((sub) => {
                      const allTasks = sub.groups.flatMap((g) => g.tasks);
                      const total = allTasks.length;
                      const counts: Record<string, number> = {};
                      allTasks.forEach((t) => {
                        const label = t.extras.status || "";
                        counts[label] = (counts[label] || 0) + 1;
                      });
                      const segments = Object.entries(counts);
                      return (
                        <div key={sub.id} className="space-y-1">
                          <button
                            onClick={() => {
                              setActiveSection(section.id);
                              setActiveSub({
                                sectionId: section.id,
                                subId: sub.id,
                              });
                            }}
                            className="w-full text-left px-4 py-2 rounded bg-gray-200"
                          >
                            {sub.title}
                          </button>
                          <div className="w-full h-2 flex rounded overflow-hidden">
                            {total === 0 ? (
                              <div
                                className="flex-1"
                                style={{ backgroundColor: "#dedede" }}
                              />
                            ) : (
                              segments.map(([label, count]) => (
                                <div
                                  key={label}
                                  style={{
                                    width: `${(count / total) * 100}%`,
                                    backgroundColor: getStatusColor(
                                      section.id,
                                      label,
                                    ),
                                  }}
                                />
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
      </div>
      {labelPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-64">
            <h3 className="font-bold mb-2">Add Status Label</h3>
            <input
              type="text"
              placeholder="Label name"
              className="border w-full p-1 mb-2"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
            />
            <input
              type="color"
              className="mb-2"
              value={newLabelColor}
              onChange={(e) => setNewLabelColor(e.target.value)}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setLabelPopup(null)}
                className="px-2 py-1 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newLabelName) return;
                  const newOpt = { label: newLabelName, color: newLabelColor };
                  addCustomLabel(labelPopup.sectionId, newOpt);
                  if (labelPopup.subTaskId) {
                    updateSubTaskStatus(
                      labelPopup.sectionId,
                      labelPopup.subId,
                      labelPopup.groupId,
                      labelPopup.taskId,
                      labelPopup.subTaskId,
                      newLabelName,
                    );
                  } else {
                    updateTaskExtra(
                      labelPopup.sectionId,
                      labelPopup.subId,
                      labelPopup.groupId,
                      labelPopup.taskId,
                      labelPopup.columnId,
                      newLabelName,
                    );
                  }
                  setLabelPopup(null);
                }}
                className="px-2 py-1 border rounded bg-blue-600 text-white"
              >
                Save
              </button>
            </div>
            {getSectionCustomOptions(sections, labelPopup.sectionId).length > 0 && (
              <div className="mt-4">
                <h4 className="font-bold mb-1">Custom Labels</h4>
                <ul className="space-y-1">
                  {getSectionCustomOptions(sections, labelPopup.sectionId).map((opt) => (
                    <li
                      key={opt.label}
                      className="flex items-center justify-between"
                    >
                      <span
                        className="px-2 py-1 rounded text-white"
                        style={{ backgroundColor: opt.color }}
                      >
                        {opt.label}
                      </span>
                      <button
                        onClick={() => deleteLabel(labelPopup.sectionId, opt.label)}
                        className="text-red-600"
                      >
                        🗑️
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      {commentPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setCommentPopup(null)}
        >
          <div
            className="bg-white p-4 rounded shadow w-[600px] max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold">Comments</h3>
              <button onClick={() => setCommentPopup(null)}>✖️</button>
            </div>
            {(() => {
              const sec = sections.find((s) => s.id === commentPopup.sectionId);
              const sub = sec?.subsections.find((s) => s.id === commentPopup.subId);
              const group = sub?.groups.find((g) => g.id === commentPopup.groupId);
              const task = group?.tasks.find((t) => t.id === commentPopup.taskId);
              const comments = task?.comments || [];
              return (
                <div className="space-y-2 mb-2">
                  {comments.map((c) => (
                    <div
                      key={c.id}
                      className="border p-2 rounded bg-gray-100 relative group"
                    >
                      {editingComment && editingComment.commentId === c.id ? (
                        <>
                          <textarea
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            className="border w-full p-1 mb-1"
                            onPaste={(e) =>
                              handleLinkPaste(e, setEditCommentText)
                            }
                          />
                          <input
                            type="file"
                            multiple
                            ref={editCommentFileRef}
                            onChange={(e) => setEditCommentFiles(e.target.files)}
                            className="hidden"
                          />
                          <button
                            onClick={() => editCommentFileRef.current?.click()}
                            className="text-blue-600 text-sm mb-1"
                          >
                            +
                          </button>
                          {editCommentFiles && (
                            <div className="text-xs text-gray-500 mb-1">
                              {Array.from(editCommentFiles)
                                .map((f) => f.name)
                                .join(", ")}
                            </div>
                          )}
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingComment(null)}
                              className="px-1 py-0.5 border rounded"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() =>
                                saveCommentEdit(
                                  commentPopup.sectionId,
                                  commentPopup.subId,
                                  commentPopup.groupId,
                                  commentPopup.taskId,
                                  c.id,
                                )
                              }
                              className="px-1 py-0.5 border rounded bg-blue-600 text-white"
                            >
                              Save
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-xs text-gray-500 mb-1">
                            {c.author} - {new Date(c.timestamp).toLocaleString()}
                          </div>
                          <div>{c.text}</div>
                          {c.attachments.map((a, i) => (
                            <div key={i} className="mt-1 relative">
                              {/* Image previews and PDF embeds have been removed. */}
                              <a
                                href={a.url}
                                download={a.name}
                                className="block text-blue-600 underline text-xs"
                              >
                                {a.name}
                              </a>
                            </div>
                          ))}
                          <button
                            onClick={() =>
                              confirmCommentDelete(
                                commentPopup.sectionId,
                                commentPopup.subId,
                                commentPopup.groupId,
                                commentPopup.taskId,
                                c.id,
                              )
                            }
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-600"
                          >
                            🗑️
                          </button>
                          {user?.name === c.author && (
                            <button
                              onClick={() => {
                                setEditingComment({
                                  sectionId: commentPopup.sectionId,
                                  subId: commentPopup.subId,
                                  groupId: commentPopup.groupId,
                                  taskId: commentPopup.taskId,
                                  commentId: c.id,
                                });
                                setEditCommentText(c.text);
                                setEditCommentFiles(null);
                              }}
                              className="text-blue-600 text-xs mt-1"
                            >
                              Edit
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="flex mb-2">
              <textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                className="border w-full p-1"
                onPaste={(e) => handleLinkPaste(e, setNewCommentText)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    addComment(
                      commentPopup.sectionId,
                      commentPopup.subId,
                      commentPopup.groupId,
                      commentPopup.taskId,
                    );
                    setCommentPopup(commentPopup);
                  }
                }}
              />
              <input
                type="file"
                multiple
                ref={newCommentFileRef}
                onChange={(e) => setNewCommentFiles(e.target.files)}
                className="hidden"
              />
              <button
                onClick={() => newCommentFileRef.current?.click()}
                className="ml-1 px-2 text-blue-600"
              >
                +
              </button>
            </div>
            {newCommentFiles && (
              <div className="mb-2 text-xs text-gray-500">
                {Array.from(newCommentFiles)
                  .map((f) => f.name)
                  .join(", ")}
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <button onClick={() => setCommentPopup(null)} className="px-2 py-1 border rounded">
                Close
              </button>
              <button
                onClick={() => {
                  addComment(
                    commentPopup.sectionId,
                    commentPopup.subId,
                    commentPopup.groupId,
                    commentPopup.taskId,
                  );
                  setCommentPopup(commentPopup); // keep open
                }}
                className="px-2 py-1 border rounded bg-blue-600 text-white"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {columnPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-64">
            <h3 className="font-bold mb-2">Add Column</h3>
            <select
              value={newColumnType}
              onChange={(e) =>
                setNewColumnType(
                  e.target.value as "text" | "person" | "status" | "date",
                )
              }
              className="border w-full p-1 mb-2"
            >
              <option value="text">Text</option>
              <option value="person">Person</option>
              <option value="status">Status</option>
              <option value="date">Date</option>
            </select>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setColumnPopup(null)}
                className="px-2 py-1 border rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (columnPopup) {
                    addColumn(columnPopup.sectionId, columnPopup.subId, columnPopup.groupId, newColumnType);
                  }
                  setColumnPopup(null);
                }}
                className="px-2 py-1 border rounded bg-blue-600 text-white"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {linkPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-72" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2">
              <label className="block text-sm mb-1">Write or paste a link</label>
              <input
                type="text"
                className="border w-full p-1"
                value={linkPopup.url}
                onChange={(e) =>
                  setLinkPopup({ ...linkPopup, url: e.target.value })
                }
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm mb-1">Text to display</label>
              <input
                type="text"
                className="border w-full p-1"
                value={linkDisplay}
                onChange={(e) => setLinkDisplay(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setLinkPopup(null)} className="px-2 py-1 border rounded">
                Cancel
              </button>
              <button
                onClick={() => {
                  linkPopup.onInsert(linkDisplay || linkPopup.url);
                  setLinkPopup(null);
                }}
                className="px-2 py-1 border rounded bg-blue-600 text-white"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
      {personPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setPersonPopup(null)}
        >
          <div className="bg-white p-4 rounded shadow w-64" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold mb-2">Assign Person</h3>
            <div className="space-y-1 mb-2">
              {people.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    updateTaskExtra(
                      personPopup.sectionId,
                      personPopup.subId,
                      personPopup.groupId,
                      personPopup.taskId,
                      personPopup.columnId,
                      p.id,
                    );
                    setPersonPopup(null);
                  }}
                  className="flex items-center space-x-2 w-full text-left px-2 py-1 rounded hover:bg-gray-100"
                >
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-black"
                    style={{ backgroundColor: p.color }}
                  >
                    {getInitials(p.name)}
                  </span>
                  <span>{p.name}</span>
                </button>
              ))}
            </div>
            <div className="border-t pt-2 mt-2 space-y-2">
              <button
                onClick={() => {
                  updateTaskExtra(
                    personPopup.sectionId,
                    personPopup.subId,
                    personPopup.groupId,
                    personPopup.taskId,
                    personPopup.columnId,
                    "",
                  );
                  setPersonPopup(null);
                }}
                className="text-red-600 text-sm w-full text-left"
              >
                Remove Assignment
              </button>
              <div className="flex justify-end">
                <button
                  onClick={() => setPersonPopup(null)}
                  className="px-2 py-1 border rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-64">
            <p className="mb-4">Are you sure you want to delete?</p>
            <div className="flex justify-end space-x-2">
              <button onClick={handleConfirmDelete} className="px-2 py-1 border rounded bg-red-600 text-white">
                Yes, delete
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 border rounded">
                No, keep
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskListDashboard;
